import {
	ApplicationError,
	ForManagingEntitlements,
	type Item,
	type Plan,
	PlanAcquisition
} from 'core'

import { HTTPClient, HTTPMethods } from './http_client'
import { AcquiredPlanResponseDTO, type PlanDTO } from './types'

export class EntitlementManager implements ForManagingEntitlements {
	readonly client = new HTTPClient()

	async getPlans() {
		const response = await this.client.fetch<PlanDTO[]>(
			`/authv2/entitlements/api/v1/plans`
		)

		if (response.error) return { error: response.error }

		return {
			data: response?.data!.map(this.formatPlan)
		}
	}

	async acquirePlan(planId: string) {
		const response = await this.client.fetch<
			AcquiredPlanResponseDTO,
			{ plan_id: string }
		>(`/authv2/entitlements/api/v1/plans/acquire`, {
			method: HTTPMethods.Post,
			data: { plan_id: planId }
		})

		if (response.error) return { error: response.error }

		const data: PlanAcquisition = {
			cartId: response.data!.cart_id,
			clientSecret: response.data!.client_secret!,
			paymentId: response.data!.payment_id!,
			returnUrl: response.data!.return_url,
			plan: this.formatPlan(response.data!.plan),
			subscriptionId: response.data!.subscription_id
		}

		return {
			data
		}
	}

	private formatPlan(plan: PlanDTO): Plan {
		return {
			active: plan.active,
			description: plan.description,
			id: plan.id,
			listItems: plan.list_items.flatMap(item =>
				item.items.map(planItem => {
					const i: Item = {
						currencyType: planItem.currency_type,
						currencyUnit: planItem.currency_unit,
						currencyValue: planItem.currency_value,
						id: planItem.id,
						quotas: planItem.quotas
					}

					if (planItem.entitlement) {
						i.entitlement = {
							currencyType: planItem.entitlement.currency_type,
							id: planItem.entitlement.id,
							product: {
								id: planItem.entitlement.product.id,
								name: planItem.entitlement.product.name
							},
							unit: planItem.entitlement.unit
						}
					}

					return i
				})
			),
			name: plan.name,
			needPayNow: plan.need_pay_now,
			total: plan.total,
			type: plan.type
		}
	}
}

export class EntitlementManagerMock implements ForManagingEntitlements {
	getPlans(): Promise<{
		data?: Plan[]
		error?: Error
	}> {
		return Promise.resolve({
			data: [
				{
					active: true,
					description: 'All functionalities with some limits',
					id: '1',
					listItems: [
						{
							currencyType: 'usd',
							currencyUnit: '',
							currencyValue: 100,
							entitlement: {
								currencyType: 'fiat',
								id: '1',
								product: {
									id: '1',
									name: 'projects'
								},
								unit: 'lifetime'
							},
							id: '1',
							quotas: 0
						},
						{
							currencyType: 'usd',
							currencyUnit: '',
							currencyValue: 50000,
							entitlement: {
								currencyType: 'fiat',
								id: '2',
								product: {
									id: '2',
									name: 'deployments'
								},
								unit: 'lifetime'
							},
							id: '2',
							quotas: 0
						},
						{
							currencyType: '',
							currencyUnit: '',
							currencyValue: 0,
							entitlement: {
								currencyType: 'fiat',
								id: '2',
								product: {
									id: '2',
									name: 'deployments'
								},
								unit: 'lifetime'
							},
							id: '3',
							quotas: 0
						}
					],
					name: 'Base',
					needPayNow: true,
					total: 50100,
					type: 'paid'
				},
				{
					active: true,
					description: 'Free forever',
					id: '2',
					listItems: [
						{
							currencyType: 'usd',
							currencyUnit: '',
							currencyValue: 0,
							entitlement: {
								currencyType: 'fiat',
								id: '3',
								product: {
									id: '1',
									name: 'projects'
								},
								unit: 'usage'
							},
							id: '4',
							quotas: 5
						},
						{
							currencyType: 'usd',
							currencyUnit: '',
							currencyValue: 0,
							entitlement: {
								currencyType: 'fiat',
								id: '5',
								product: {
									id: '1',
									name: 'projects'
								},
								unit: 'usage'
							},
							id: '5',
							quotas: 5
						}
					],
					name: 'Life time free plan',
					needPayNow: false,
					total: 0,
					type: 'free'
				}
			]
		})
	}

	acquirePlan(): Promise<{
		data?: PlanAcquisition
		error?: ApplicationError
	}> {
		throw new Error('Method not implemented.')
	}
}
